import {createSelector} from 'reselect';

import {DocumentsPartName, ProductSheetState, SectionsPartName} from '@common/types/stores/productSheetStore.types';
import {Uuid} from '@common/types/index.types';

const createProductSheetSelector = createSelector.withTypes<ProductSheetState>();

const selectDocuments = createProductSheetSelector([state => state], state => state.documents.current);
const selectSections = createProductSheetSelector([state => state], state => state.sections.current);
const selectDocumentsToCompare = createProductSheetSelector([state => state], state => state.documents.compare);
const selectSectionsToCompare = createProductSheetSelector([state => state], state => state.sections.compare);

export const selectClaimIntentionByUuid = createProductSheetSelector([state => state, (_state, uuid: Uuid) => uuid], (state, uuid) => state.claimIntentions[uuid]);
export const selectClaimByUuid = createProductSheetSelector([state => state, (_state, uuid: Uuid) => uuid], (state, uuid) => state.claims[uuid]);

export const selectActiveUuid = createProductSheetSelector([selectDocuments], documents => documents.activeUuid ?? undefined);
export const selectDocumentsPart = createProductSheetSelector([state => state, (_state, documentsPartName: DocumentsPartName = 'current') => documentsPartName], (state, documentsPartName) => {
  return state.documents[documentsPartName];
});
export const selectProduct = createProductSheetSelector([selectDocumentsPart], documentsPart => {
  return documentsPart?.product || undefined;
});
export const selectGmc = createProductSheetSelector([selectDocumentsPart], documentsPart => {
  return documentsPart?.gmc || undefined;
});
export const selectRmc = createProductSheetSelector([selectDocumentsPart], documentsPart => {
  return documentsPart?.rmc || undefined;
});
export const selectRegion = createProductSheetSelector([selectDocumentsPart], documentsPart => {
  return documentsPart?.region || undefined;
});
export const selectCountry = createProductSheetSelector([selectDocumentsPart], documentsPart => {
  return documentsPart?.country || undefined;
});
export const selectProductUuid = createProductSheetSelector([selectDocuments], documents => documents.product?.id);
export const selectProductUuid2 = createProductSheetSelector(
  [state => state, (_state, documentsPartName: DocumentsPartName = 'current') => documentsPartName],
  (state, documentsPartName) => state.documents[documentsPartName]?.product?.id
);
export const selectGmcUuid = createProductSheetSelector([selectDocuments], documents => documents.gmc?.id);
export const selectGmcUuid2 = createProductSheetSelector(
  [state => state, (_state, documentsPartName: DocumentsPartName = 'current') => documentsPartName],
  (state, documentsPartName) => state.documents[documentsPartName]?.gmc?.id
);
export const selectRmcUuid = createProductSheetSelector(
  [state => state, (_state, documentsPartName: DocumentsPartName = 'current') => documentsPartName],
  (state, documentsPartName) => state.documents[documentsPartName]?.rmc?.id
);

export const selectRegionUuid = createProductSheetSelector(
  [state => state, (_state, documentsPartName: DocumentsPartName = 'current') => documentsPartName],
  (state, documentsPartName) => state.documents[documentsPartName]?.region?.id
);

export const selectCountryUuid = createProductSheetSelector(
  [state => state, (_state, documentsPartName: DocumentsPartName = 'current') => documentsPartName],
  (state, documentsPartName) => state.documents[documentsPartName]?.country?.id
);

const selectProductSection = createProductSheetSelector([selectSections, selectProductUuid], (sections, uuid) => (uuid ? sections[uuid] : undefined));
const selectGmcSection = createProductSheetSelector([selectSections, selectGmcUuid], (sections, uuid) => (uuid ? sections[uuid] : undefined));
const selectActiveSections = createProductSheetSelector([selectSections, selectActiveUuid], (sections, uuid) => (uuid ? sections[uuid] : undefined));
const selectProductInformation = createProductSheetSelector([selectProductSection], sections => sections?.information);

export const selectProductInformation2 = createProductSheetSelector(
  [state => state, (_state, sectionPartName: SectionsPartName) => sectionPartName, (_state, _sectionPartName: SectionsPartName, uuid: Uuid) => uuid],
  (state, sectionPartName, uuid) => {
    return state.sections[sectionPartName][uuid]?.information;
  }
);

const selectGmcInformation = createProductSheetSelector([selectGmcSection], sections => sections?.gmcInformation);

export const selectActiveBucket = createProductSheetSelector([selectActiveSections], sections => sections?.bucket);

export const selectName = createProductSheetSelector([selectGmcUuid, selectGmcInformation, selectProduct], (gmcUuid, gmcInformation, product) => (gmcUuid ? gmcInformation?.name : product?.name));
export const selectIsNameLoading = createProductSheetSelector(
  [selectGmcUuid, selectGmcInformation, selectProductInformation],
  (gmcUuid, gmcInformation, productInformation) => (gmcUuid ? gmcInformation?.isLoading : productInformation?.isLoading) || false
);
export const selecIsLoadingResolveContext = createProductSheetSelector([state => state], state => state.isLoadingResolveContext);

// Compare
export const selectActiveUuidToCompare = createProductSheetSelector([selectDocumentsToCompare], documents => documents?.activeUuid ?? undefined);
export const selectProductUuidToCompare = createProductSheetSelector([selectDocumentsToCompare], documents => documents?.product?.id);
export const selectGmcUuidToCompare = createProductSheetSelector([selectDocumentsToCompare], documents => documents?.gmc?.id);
const selectProductSectionToCompare = createProductSheetSelector([selectSectionsToCompare, selectProductUuidToCompare], (sections, productUuid) => (productUuid ? sections[productUuid] : undefined));
const selectGmcSectionToCompare = createProductSheetSelector([selectSectionsToCompare, selectGmcUuidToCompare], (sections, gmcUuid) => (gmcUuid ? sections[gmcUuid] : undefined));
const selectProductInformationToCompare = createProductSheetSelector([selectProductSectionToCompare], sections => sections?.information);
const selectGmcInformationToCompare = createProductSheetSelector([selectGmcSectionToCompare], sections => sections?.gmcInformation);
export const selectNameToCompare = createProductSheetSelector(
  [selectGmcUuidToCompare, selectGmcInformationToCompare, selectProductInformationToCompare],
  (gmcUuid, gmcInformation, productInformation) => (gmcUuid ? gmcInformation?.name : productInformation?.name)
);
export const selectIsNameLoadingToCompare = createProductSheetSelector(
  [selectGmcUuidToCompare, selectGmcInformationToCompare, selectProductInformationToCompare],
  (gmcUuid, gmcInformation, productInformation) => (gmcUuid ? gmcInformation?.isLoading : productInformation?.isLoading) || false
);

export const selectInformationState = createProductSheetSelector(
  [state => state, (_state, sectionPartName: SectionsPartName) => sectionPartName, state => state.documents],
  (state, sectionPartName, documents) => {
    const uuid = documents[sectionPartName]?.activeUuid;
    if (!uuid) {
      return undefined;
    }
    if (documents[sectionPartName]?.rmc) {
      return state.sections[sectionPartName][uuid]?.rmcInformation?.status;
    } else if (documents[sectionPartName]?.gmc) {
      return state.sections[sectionPartName][uuid]?.gmcInformation?.status;
    }
    return undefined;
  }
);

export const selectInformationStatus = createProductSheetSelector([state => state, (_state, sectionPartName: SectionsPartName) => sectionPartName], (state, sectionPartName) => {
  const activeUuid = state.documents[sectionPartName]?.activeUuid;
  const productUuid = state.documents[sectionPartName]?.product?.id;
  if (activeUuid) {
    return state.sections[sectionPartName][activeUuid]?.bucket?.status;
  } else {
    if (productUuid) {
      return state.documents[sectionPartName]?.product?.status;
    }
    return undefined;
  }
});

export const selectInformationSection = createProductSheetSelector(
  [state => state, (_state, sectionPartName: SectionsPartName) => sectionPartName, (_state, _sectionPartName: SectionsPartName, uuid?: Uuid) => uuid],
  (state, sectionPartName, uuid) => {
    return uuid ? state.sections[sectionPartName][uuid]?.information : null;
  }
);

export const selectBreedsSection = createProductSheetSelector(
  [state => state, (_state, sectionPartName: SectionsPartName) => sectionPartName, (_state, _sectionPartName: SectionsPartName, uuid: Uuid) => uuid],
  (state, sectionPartName, uuid) => {
    return state.sections[sectionPartName][uuid]?.breeds;
  }
);

export const selectTeamSection = createProductSheetSelector(
  [state => state, (_state, sectionPartName: SectionsPartName) => sectionPartName, (_state, _sectionPartName: SectionsPartName, uuid: Uuid) => uuid],
  (state, sectionPartName, uuid) => {
    return state.sections[sectionPartName][uuid]?.team;
  }
);

export const selectGmcInformationSection = createProductSheetSelector([state => state, (_state, sectionPartName: SectionsPartName) => sectionPartName], (state, sectionPartName) => {
  const uuidKey = state.documents[sectionPartName]?.gmc?.id;
  return uuidKey ? state.sections[sectionPartName][uuidKey]?.gmcInformation : undefined;
});

export const selectRmcInformationSection = createProductSheetSelector([state => state, (_state, sectionPartName: SectionsPartName) => sectionPartName], (state, sectionPartName) => {
  const uuidKey = state.documents[sectionPartName]?.rmc?.id;
  return uuidKey ? state.sections[sectionPartName][uuidKey]?.rmcInformation : undefined;
});

export const selectSectionsByUuid = createProductSheetSelector(
  [
    state => state,
    (_state, sectionPartName: SectionsPartName) => sectionPartName,
    (_state, _sectionPartName: SectionsPartName, uuid?: Uuid) => uuid,
    (_state, _sectionPartName: SectionsPartName, _uuid?: Uuid, options?: {countryUuid?: Uuid | null}) => options
  ],
  (state, sectionPartName, uuid, options = {}) => {
    const {countryUuid} = options;
    const uuidKey = countryUuid ?? uuid;
    return uuidKey ? state.sections[sectionPartName][uuidKey] : undefined;
  }
);

/**
 * Returns then branding section according to the country overload
 */
export const selectBrandingSection = createProductSheetSelector([selectSectionsByUuid], sections => sections?.branding);

/**
 * Returns then branding section loading status according to the country overload
 */
export const selectBrandingSectionIsLoading = createProductSheetSelector([selectBrandingSection], branding => branding?.isLoading || false);

/**
 * Returns then branding section edit loading status according to the country overload
 */
export const selectBrandingSectionIsEditLoading = createProductSheetSelector([selectBrandingSection], branding => branding?.isEditLoading || false);

/**
 * Returns then gmcInformation section edit loading status
 */
export const selectGmcInformationSectionIsEditLoading = createProductSheetSelector([selectGmcInformation], gmcInformation => gmcInformation?.isEditLoading || false);

/**
 * Returns then feeding guides section according to the country overload
 */
export const selectFeedingGuidesSection = createProductSheetSelector([selectSectionsByUuid], sections => sections?.feedingGuides);

/**
 * Returns then feeding guides section loading status according to the country overload
 */
export const selectFeedingGuidesSectionIsLoading = createProductSheetSelector([selectFeedingGuidesSection], feedingGuides => feedingGuides?.isLoading || false);

/**
 * Returns then feeding guides section edit loading status according to the country overload
 */
export const selectFeedingGuidesSectionIsEditLoading = createProductSheetSelector([selectFeedingGuidesSection], feedingGuides => feedingGuides?.isEditLoading || false);

/**
 * Returns the Nutritional Legal Element section according to the country overload
 */
export const selectNutritionalLegalElementSection = createProductSheetSelector([selectSectionsByUuid], sections => sections?.nutritionalLegalElements);

/**
 * Returns then Nutritional Legal Element section loading status according to the country overload
 */
export const selectNutritionalLegalElementSectionIsLoading = createProductSheetSelector(
  [selectNutritionalLegalElementSection],
  nutritionalLegalElements => nutritionalLegalElements?.isLoading || false
);

/**
 * Returns then Nutritional Legal Element section edit loading status according to the country overload
 */
export const selectNutritionalLegalElementSectionIsEditLoading = createProductSheetSelector(
  [selectNutritionalLegalElementSection],
  nutritionalLegalElements => nutritionalLegalElements?.isEditLoading || false
);

/**
 * Returns the Packaging Features section according to the country overload
 */
export const selectPackagingFeaturesSection = createProductSheetSelector([selectSectionsByUuid], sections => sections?.packagingFeatures);

/**
 * Returns then Packaging Features section loading status according to the country overload
 */
export const selectPackagingFeaturesSectionIsLoading = createProductSheetSelector([selectPackagingFeaturesSection], packagingFeatures => packagingFeatures?.isLoading || false);

/**
 * Returns then Packaging Features section edit loading status according to the country overload
 */
export const selectPackagingFeaturesSectionIsEditLoading = createProductSheetSelector([selectPackagingFeaturesSection], packagingFeatures => packagingFeatures?.isEditLoading || false);

/**
 * Returns then Product Media section according to the country overload
 */
export const selectProductMediaSection = createProductSheetSelector([selectSectionsByUuid], sections => sections?.productMedia);

/**
 * Returns then Product Media section loading status according to the country overload
 */
export const selectProductMediaSectionIsLoading = createProductSheetSelector([selectProductMediaSection], productMedia => productMedia?.isLoading || false);

/**
 * Returns then Product Media section edit loading status according to the country overload
 */
export const selectProductMediaSectionIsEditLoading = createProductSheetSelector([selectProductMediaSection], productMedia => productMedia?.isEditLoading || false);

/**
 * Returns then Other Legal Elements section according to the country overload
 */
export const selectOtherLegalElementsSection = createProductSheetSelector([selectSectionsByUuid], sections => sections?.otherLegalElements);

/**
 * Returns then Other Legal Elements section loading status according to the country overload
 */
export const selectOtherLegalElementsSectionIsLoading = createProductSheetSelector([selectOtherLegalElementsSection], otherLegalElements => otherLegalElements?.isLoading || false);

/**
 * Returns then Other Legal Elements section edit loading status according to the country overload
 */
export const selectOtherLegalElementsSectionIsEditLoading = createProductSheetSelector([selectOtherLegalElementsSection], otherLegalElements => otherLegalElements?.isEditLoading || false);

/**
 * Returns then Product Features section according to the country overload
 */
export const selectProductFeaturesSection = createProductSheetSelector([selectSectionsByUuid], sections => sections?.productFeatures);

/**
 * Returns then Product Features section loading status according to the country overload
 */
export const selectProductFeaturesSectionIsLoading = createProductSheetSelector([selectProductFeaturesSection], productFeatures => productFeatures?.isLoading || false);

/**
 * Returns then Product Features section edit loading status according to the country overload
 */
export const selectProductFeaturesSectionIsEditLoading = createProductSheetSelector([selectProductFeaturesSection], productFeatures => productFeatures?.isEditLoading || false);

/**
 * Returns then skus section
 */
export const selectSkusSection = createProductSheetSelector([selectSectionsByUuid], sections => sections?.skus);

export const selectGmcBucketFiles = createProductSheetSelector([selectGmcSection], selectGmcSection => selectGmcSection?.bucket?.files);

export const selectSelectedClaims = createProductSheetSelector([state => state], state => state.selectedClaims);

/**
 * Returns then compositions section
 */
export const selectCompositionSection = createProductSheetSelector([selectSectionsByUuid], sections => sections?.compositions);

export const selectCompositionSectionCollection = createProductSheetSelector([selectCompositionSection], selectCompositionSection => selectCompositionSection?.collection);

export const selectCompositionSectionIsLoading = createProductSheetSelector([selectCompositionSection], selectCompositionSection => selectCompositionSection?.isLoading);

export const selectIsGmcSectionsLoading = createProductSheetSelector(
  [selectBrandingSectionIsLoading, selectFeedingGuidesSectionIsLoading, selectPackagingFeaturesSectionIsLoading, selectOtherLegalElementsSectionIsLoading, selectProductFeaturesSectionIsLoading],
  isLoading => isLoading || false
);

export const selectIsRmcSectionsLoading = createProductSheetSelector(
  [
    selectBrandingSectionIsLoading,
    selectFeedingGuidesSectionIsLoading,
    selectPackagingFeaturesSectionIsLoading,
    selectOtherLegalElementsSectionIsLoading,
    selectProductFeaturesSectionIsLoading,
    selectNutritionalLegalElementSectionIsLoading
  ],
  isLoading => isLoading || false
);

export const selectIsGmcVersionLoading = createProductSheetSelector([state => state], state => state.isGmcVersionLoading || false);
